import { Link } from '@mui/material';
import { t } from 'i18next';
import { ReactElement } from 'react';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { AuthorizationError } from '../../../common/errors/errors';
import { KEY_LENGTH_OPTIONS, KEY_LENGTH_OPTION_VALIDATOR } from '../../../common/keys/constants/KeyLengths';
import { KeyCategory, getKeyCategoryLabel } from '../../../common/keys/enums/KeyCategory';
import { KeyType, getKeyTypeLabel } from '../../../common/keys/enums/KeyType';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { PATHS, TabNameLocalizationKeys } from '../../../routing/routes';
import { OrganizationKey } from '../models/OrganizationKey';
import { OrganizationKeyService } from '../services/OrganizationKeysService';
import { bind } from '../../../common/utils/decorators';

type State = PaginatedTableState & {};

type Props = {};

export class OrganizationKeysController extends PaginatedTableController<OrganizationKey, Props, State> {
  private readonly keyService = new OrganizationKeyService();

  componentWillUnmount() {
    super.componentWillUnmount();

    this.keyService.cancelAllRequests();
  }

  protected doInitialDataFetch(): void {
    // do not fetch until permissions are checked
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const permission = await this.permissionsService.canAccessOrganizationKey();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }

    super.doInitialDataFetch();
  }

  protected renderTable(tableComponent: ReactElement) {
    return (
      <SecondaryNav title={t(TabNameLocalizationKeys.KEYS)} subtitle={t('keysListSubtitle')} getBreadcrumbs={this.getBreadcrumbs}>
        {tableComponent}
      </SecondaryNav>
    );
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'category',
        label: t('category'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(KeyCategory).map((value) => ({ value, label: getKeyCategoryLabel(value) })),
      },
      {
        filterKey: 'type',
        label: t('type'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(KeyType).map((value) => ({ value, label: getKeyTypeLabel(value) })),
      },
      {
        filterKey: 'length',
        label: t('keyLength'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.GE, label: t('filterLabels.isGreaterOrEqual') },
          { value: Operation.GT, label: t('filterLabels.isGreater') },
          { value: Operation.LE, label: t('filterLabels.isLessOrEqual') },
          { value: Operation.LT, label: t('filterLabels.isLess') },
        ],
        options: KEY_LENGTH_OPTIONS(),
        validator: KEY_LENGTH_OPTION_VALIDATOR,
      },
    ];
  }

  protected getFieldConfigurations(): FieldConfiguration<OrganizationKey>[] {
    return [
      {
        fieldKey: 'id',
        label: t('id'),
        description: t('id'),
        columnProps: {
          maxWidth: 100,
        },
        render: (model, { tabIndex }) => (
          <Link
            tabIndex={tabIndex}
            href={UrlHelper.generatePath(PATHS.KEY, { keyId: model.getId().toString() })}
            aria-label={t('a11y.organization.keys.keyIdLabel', { keyId: model.getId() }) as string}
          >
            {model.getId()}
          </Link>
        ),
      },
      {
        fieldKey: 'shortDescription',
        columnProps: {
          maxWidth: 400,
        },
        label: t('type'),
        description: t('type'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{model.getShortDescription()}</TypographyOverflow>,
      },
      {
        fieldKey: 'information',
        label: t('keyInformation'),
        description: t('keyInformation'),
        render: (model, { hasFocus }) => {
          const information = model.getInformation();
          if (information) {
            return <TypographyOverflow hasFocus={hasFocus}>{information.join(', ')}</TypographyOverflow>;
          }
          return <BlankCell blankText={t('noInformation')} />;
        },
      },
      {
        fieldKey: 'matchedKeysCount',
        label: t('matchedKeys'),
        description: t('matchedKeys'),
        columnProps: {
          maxWidth: 200,
        },
        render: (model) => model.getMatchedKeysCount(),
      },
    ];
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<OrganizationKey>> {
    try {
      return await this.keyService.getList(cursorInfo, this.getFilters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getKeysBreadcrumbs();
  }
}
